import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { DebtState, Debt, DebtProjection, InterestAccumulation, InterestAccumulationPoint } from '@/types'

// Helper function to calculate debt payoff projections
const calculateDebtProjections = (debts: Debt[]): { projections: DebtProjection[]; interestAccumulation: InterestAccumulation[] } => {
  const projections: DebtProjection[] = []
  const interestAccumulation: InterestAccumulation[] = []

  debts.forEach((debt) => {
    const monthlyRate = debt.interestRate / 100 / 12
    const payment = debt.monthlyPayment
    const balance = debt.balance

    if (monthlyRate === 0 || payment <= balance * monthlyRate) {
      projections.push({
        debtId: debt.id,
        debtName: debt.name,
        monthsToPayoff: '∞',
        totalInterest: 0,
        totalCost: balance,
        monthlyInterest: 0,
        isAccumulating: payment <= balance * monthlyRate,
      })
      return
    }

    let monthsToPayoff = 0
    let totalInterest = 0
    let currentBalance = balance

    while (currentBalance > 0 && monthsToPayoff < 600) {
      const interest = currentBalance * monthlyRate
      const principalPayment = Math.min(payment - interest, currentBalance)
      currentBalance = Math.max(0, currentBalance - principalPayment)
      totalInterest += interest
      monthsToPayoff++
    }

    const monthlyInterest = balance * monthlyRate

    projections.push({
      debtId: debt.id,
      debtName: debt.name,
      monthsToPayoff: monthsToPayoff >= 600 ? '∞' : monthsToPayoff,
      totalInterest,
      totalCost: balance + totalInterest,
      monthlyInterest,
      isAccumulating: payment <= balance * monthlyRate,
    })

    // Calculate interest accumulation over time
    const accumulation: InterestAccumulationPoint[] = []
    currentBalance = balance
    for (let month = 1; month <= Math.min(60, monthsToPayoff); month++) {
      const interest = currentBalance * monthlyRate
      const principalPayment = Math.min(payment - interest, currentBalance)
      currentBalance = Math.max(0, currentBalance - principalPayment)

      accumulation.push({
        month,
        balance: currentBalance,
        interestPaid: interest,
        totalInterestPaid: accumulation.reduce((sum, item) => sum + item.interestPaid, 0) + interest,
      })
    }

    interestAccumulation.push({
      debtId: debt.id,
      debtName: debt.name,
      accumulation,
    })
  })

  return { projections, interestAccumulation }
}

const initialState: DebtState = {
  debts: [],
  totalBalance: 0,
  totalMonthlyPayments: 0,
  averageInterestRate: 0,
  totalCreditLimit: 0,
  totalCreditUtilization: 0,
  highInterestDebts: 0,
  creditCardDebts: 0,
  totalMinimumPayments: 0,
  debtAnalysis: {
    totalInterestPaid: 0,
    totalInterestToPay: 0,
    totalBalance: 0,
    monthsToPayoff: 0,
    totalCost: 0,
    monthlyInterestExpense: 0,
    debtAccumulation: [],
    payoffProjections: [],
    interestAccumulation: [],
  },
}

const debtSlice = createSlice({
  name: 'debt',
  initialState,
  reducers: {
    addDebt: (state, action: PayloadAction<Omit<Debt, 'id' | 'utilizationRate' | 'minimumPayment'>>) => {
      const { creditLimit = 0, paymentPercentage = 2, ...rest } = action.payload
      const debt: Debt = {
        id: Date.now(),
        minimumPayment: 0,
        utilizationRate: 0,
        creditLimit,
        paymentPercentage,
        ...rest,
      }

      if (debt.debtType === 'credit_card') {
        debt.utilizationRate = debt.creditLimit > 0 ? (debt.balance / debt.creditLimit) * 100 : 0
        debt.minimumPayment = debt.creditLimit > 0 ? Math.max(debt.balance * 0.02, 25) : debt.monthlyPayment
      }

      state.debts.push(debt)
      debtSlice.caseReducers.updateTotals(state)
      debtSlice.caseReducers.updateDebtAnalysis(state)
    },
    updateDebt: (state, action: PayloadAction<{ id: number; updates: Partial<Debt> }>) => {
      const { id, updates } = action.payload
      const index = state.debts.findIndex((debt) => debt.id === id)
      if (index !== -1) {
        const updatedDebt = { ...state.debts[index], ...updates }

        if (updatedDebt.debtType === 'credit_card') {
          updatedDebt.utilizationRate = updatedDebt.creditLimit > 0 ? (updatedDebt.balance / updatedDebt.creditLimit) * 100 : 0
          updatedDebt.minimumPayment = updatedDebt.creditLimit > 0 ? Math.max(updatedDebt.balance * 0.02, 25) : updatedDebt.monthlyPayment
          if (!updatedDebt.paymentPercentage) {
            updatedDebt.paymentPercentage = 2
          }
        }

        state.debts[index] = updatedDebt
        debtSlice.caseReducers.updateTotals(state)
        debtSlice.caseReducers.updateDebtAnalysis(state)
      }
    },
    removeDebt: (state, action: PayloadAction<number>) => {
      state.debts = state.debts.filter((debt) => debt.id !== action.payload)
      debtSlice.caseReducers.updateTotals(state)
      debtSlice.caseReducers.updateDebtAnalysis(state)
    },
    setDebtData: (state, action: PayloadAction<Partial<DebtState>>) => {
      const data = action.payload
      if (data.debts) state.debts = data.debts
      if (data.totalBalance !== undefined) state.totalBalance = data.totalBalance
      if (data.totalMonthlyPayments !== undefined) state.totalMonthlyPayments = data.totalMonthlyPayments
      if (data.averageInterestRate !== undefined) state.averageInterestRate = data.averageInterestRate
      if (data.totalCreditLimit !== undefined) state.totalCreditLimit = data.totalCreditLimit
      if (data.totalCreditUtilization !== undefined) state.totalCreditUtilization = data.totalCreditUtilization
      if (data.highInterestDebts !== undefined) state.highInterestDebts = data.highInterestDebts
      if (data.creditCardDebts !== undefined) state.creditCardDebts = data.creditCardDebts
      if (data.totalMinimumPayments !== undefined) state.totalMinimumPayments = data.totalMinimumPayments
      if (data.debtAnalysis) {
        state.debtAnalysis = { ...state.debtAnalysis, ...data.debtAnalysis }
      }
      debtSlice.caseReducers.updateTotals(state)
      debtSlice.caseReducers.updateDebtAnalysis(state)
    },
    setDebts: (state, action: PayloadAction<Debt[]>) => {
      state.debts = action.payload
      debtSlice.caseReducers.updateTotals(state)
      debtSlice.caseReducers.updateDebtAnalysis(state)
    },
    updateTotals: (state) => {
      state.totalBalance = state.debts.reduce((sum, debt) => sum + debt.balance, 0)
      state.totalMonthlyPayments = state.debts.reduce((sum, debt) => sum + debt.monthlyPayment, 0)

      const creditCards = state.debts.filter((debt) => debt.debtType === 'credit_card')
      state.creditCardDebts = creditCards.length
      state.totalCreditLimit = creditCards.reduce((sum, debt) => sum + (debt.creditLimit || 0), 0)
      state.totalCreditUtilization =
        state.totalCreditLimit > 0
          ? (creditCards.reduce((sum, debt) => sum + debt.balance, 0) / state.totalCreditLimit) * 100
          : 0
      state.totalMinimumPayments = creditCards.reduce((sum, debt) => sum + (debt.minimumPayment || 0), 0)

      state.highInterestDebts = state.debts.filter((debt) => debt.interestRate > 15).length

      if (state.totalBalance > 0) {
        const weightedRate = state.debts.reduce((sum, debt) => sum + debt.balance * debt.interestRate, 0)
        state.averageInterestRate = weightedRate / state.totalBalance
      } else {
        state.averageInterestRate = 0
      }
    },
    updateDebtAnalysis: (state) => {
      const { projections, interestAccumulation } = calculateDebtProjections(state.debts)

      const totalInterestToPay = projections.reduce((sum, proj) => sum + proj.totalInterest, 0)
      const totalCost = state.totalBalance + totalInterestToPay
      const monthlyInterestExpense = state.debts.reduce((sum, debt) => {
        const monthlyRate = debt.interestRate / 100 / 12
        return sum + debt.balance * monthlyRate
      }, 0)

      const maxMonths = Math.max(
        ...projections.map((p) => (p.monthsToPayoff === '∞' ? 0 : (p.monthsToPayoff as number)))
      )

      state.debtAnalysis = {
        totalInterestPaid: 0,
        totalInterestToPay,
        totalBalance: state.totalBalance,
        monthsToPayoff: maxMonths,
        totalCost,
        monthlyInterestExpense,
        debtAccumulation: [],
        payoffProjections: projections,
        interestAccumulation,
      }
    },
  },
})

export const { addDebt, updateDebt, removeDebt, setDebtData, setDebts } = debtSlice.actions

export default debtSlice.reducer
